import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import { UpdateLinkRole } from "../../redux/actions/UpdateLinkRoleAction";

const EditLinkRoleModal = ({ roles, links, open, onClose, linkRoleData }) => {
  const dispatch = useDispatch();
  const status = useSelector((state) => state.updateLinkRole.status);
  const [form, setForm] = useState({ idLinkRole: "", idRole: "", idLink: "" });
  const [message, setMessage] = useState({ type: "", text: "" });
  const loading = status === "loading";

  useEffect(() => {
    if (linkRoleData && open) {
      setForm({
        idLinkRole: linkRoleData.idLinkRole ?? "",
        idRole: linkRoleData.idRole ?? "",
        idLink: linkRoleData.idLink ?? "",
      });
    }
  }, [linkRoleData, open]);

  const isFormValid = () =>
    form.idLinkRole !== "" && form.idRole !== "" && form.idLink !== "";

  const handleClose = () => {
    onClose();
    setForm({ idLinkRole: "", idLink: "", idRole: "" });
    setMessage({ type: "", text: "" });
  };

  const handleSave = async () => {
    setMessage({ type: "", text: "" });

    try {
      const result = await dispatch(
        UpdateLinkRole(form.idLinkRole, form.idRole, form.idLink)
      );

      if (result.success) {
        setMessage({
          type: "success",
          text:
            typeof result.message === "string"
              ? result.message
              : "Link role updated successfully",
        });
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        setMessage({
          type: "error",
          text:
            typeof result.message === "string"
              ? result.message
              : "There was an error updating the link role",
        });
      }
    } catch {
      setMessage({
        type: "error",
        text: "There was an error updating the link role. Please try again.",
      });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Link Role</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <TextField
            label="ID"
            value={form.idLinkRole}
            fullWidth
            disabled
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiInputBase-root": { height: "42px" },
              "& .MuiInputLabel-root": { fontSize: "1.2rem" },
            }}
          />
          <TextField
            select
            label="Role"
            value={form.idRole}
            onChange={(e) => setForm({ ...form, idRole: e.target.value })}
            fullWidth
            required
            disabled={loading}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiInputBase-root": { height: "42px" },
              "& .MuiInputLabel-root": { fontSize: "1.2rem" },
            }}
          >
            {roles.map((role) => (
              <MenuItem key={role.idRole} value={role.idRole}>
                {role.roleName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Link"
            value={form.idLink}
            onChange={(e) => setForm({ ...form, idLink: e.target.value })}
            fullWidth
            required
            disabled={loading}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiInputBase-root": { height: "42px" },
              "& .MuiInputLabel-root": { fontSize: "1.2rem" },
            }}
          >
            {links.map((link) => (
              <MenuItem key={link.idLink} value={link.idLink}>
                {link.linkName}
              </MenuItem>
            ))}
          </TextField>

          {message.text && (
            <Alert
              severity={message.type}
              onClose={() => setMessage({ type: "", text: "" })}
            >
              {message.text}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isFormValid() || loading}
          startIcon={
            loading ? <CircularProgress size={20} color="inherit" /> : null
          }
          sx={{
            backgroundColor: "#4b4bfa",
            "&:hover": { backgroundColor: "#3e3ed9" },
            color: "#fff",
          }}
        >
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditLinkRoleModal;
